import { useState, useEffect, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface ImageLightboxProps {
  open: boolean;
  onClose: () => void;
  images: string[];
  initialIndex?: number;
  venueName: string;
}

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    maxWidth: '100vw',
    maxHeight: '100vh',
    margin: 0,
    borderRadius: 0,
  },
});

const LightboxContainer = styled(Box)({
  position: 'relative',
  width: '100vw',
  height: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Header = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)',
  zIndex: 10,
});

const Title = styled(Typography)({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '1rem',
  fontWeight: 500,
  color: '#ffffff',
});

const ImageCounter = styled(Typography)({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.875rem',
  color: 'rgba(255, 255, 255, 0.8)',
});

const CloseButton = styled(IconButton)({
  color: '#ffffff',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
});

const ImageContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  padding: '80px 60px',
});

const LightboxImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  userSelect: 'none',
});

const NavigationButton = styled(IconButton)({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 56,
  height: 56,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#ffffff',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-50%) scale(1.05)',
  },
  '&:disabled': {
    color: 'rgba(255, 255, 255, 0.3)',
    backgroundColor: 'transparent',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 32,
  },
});

const PrevButton = styled(NavigationButton)({
  left: 16,
});

const NextButton = styled(NavigationButton)({
  right: 16,
});

const ThumbnailStrip = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '16px 24px',
  background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
  overflowX: 'auto',
  zIndex: 10,
});

const Thumbnail = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive }) => ({
  width: 60,
  height: 45,
  borderRadius: 4,
  overflow: 'hidden',
  border: isActive ? '2px solid #ffffff' : '2px solid transparent',
  padding: 0,
  cursor: 'pointer',
  opacity: isActive ? 1 : 0.6,
  transition: 'all 0.2s ease',
  flexShrink: 0,
  '&:hover': {
    opacity: 1,
  },
}));

const ThumbnailImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export function ImageLightbox({
  open,
  onClose,
  images,
  initialIndex = 0,
  venueName,
}: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowLeft':
          handlePrev();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'Escape':
          onClose();
          break;
      }
    },
    [open, handlePrev, handleNext, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const showThumbnails = images.length > 1 && images.length <= 20;
  const showNavigation = images.length > 1;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionProps={{
        timeout: 200,
      }}
    >
      <LightboxContainer>
        {/* Header */}
        <Header>
          <Box>
            <Title>{venueName}</Title>
            <ImageCounter>
              {currentIndex + 1} of {images.length}
            </ImageCounter>
          </Box>
          <CloseButton onClick={onClose} aria-label="Close gallery">
            <CloseIcon />
          </CloseButton>
        </Header>

        {/* Main Image */}
        <ImageContainer>
          {images.length > 0 && (
            <LightboxImage
              src={images[currentIndex]}
              alt={`${venueName} - Image ${currentIndex + 1}`}
            />
          )}
        </ImageContainer>

        {/* Navigation Arrows */}
        {showNavigation && (
          <>
            <PrevButton onClick={handlePrev} aria-label="Previous image">
              <ChevronLeftIcon />
            </PrevButton>
            <NextButton onClick={handleNext} aria-label="Next image">
              <ChevronRightIcon />
            </NextButton>
          </>
        )}

        {/* Thumbnail Strip */}
        {showThumbnails && (
          <ThumbnailStrip>
            {images.map((image, index) => (
              <Thumbnail
                key={index}
                isActive={index === currentIndex}
                onClick={() => setCurrentIndex(index)}
                aria-label={`View image ${index + 1}`}
              >
                <ThumbnailImage src={image} alt={`Thumbnail ${index + 1}`} />
              </Thumbnail>
            ))}
          </ThumbnailStrip>
        )}
      </LightboxContainer>
    </StyledDialog>
  );
}
